import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { FileText, Sparkles, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DeductionsHub() {
  const [deductions, setDeductions] = useState({
    claimed_deductions: [],
    suggested_deductions: [],
    total_claimed: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDeductions();
  }, []);

  const loadDeductions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/deductions`);
      setDeductions(response.data);
    } catch (error) {
      console.error("Error loading deductions:", error);
      toast.error("Failed to load deductions");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-4xl sm:text-5xl font-heading font-light tracking-tight" data-testid="deductions-hub-title">
          Deductions Hub
        </h1>
        <p className="text-lg text-muted-foreground mt-2">
          Maximize your tax savings with smart deduction tracking
        </p>
      </motion.div>

      {/* Total Claimed */}
      <Card className="bg-primary text-primary-foreground">
        <CardContent className="p-8">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90 mb-2">Total Deductions Claimed</p>
              <p className="text-4xl font-mono font-bold" data-testid="total-claimed-deductions">
                ${deductions.total_claimed.toLocaleString()}
              </p>
            </div>
            <FileText className="w-16 h-16 opacity-20" />
          </div>
        </CardContent>
      </Card>

      {/* Suggested Deductions */}
      <div className="space-y-6">
        <div className="flex items-center space-x-2">
          <Sparkles className="w-5 h-5 text-accent" />
          <h2 className="text-2xl font-heading font-light">Common Deductions to Track</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {deductions.suggested_deductions.map((deduction, index) => (
            <motion.div
              key={deduction.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="hover:shadow-md transition-all" data-testid={`suggested-deduction-${deduction.name.toLowerCase().replace(/\s+/g, '-')}`}>
                <CardHeader>
                  <CardTitle className="text-lg font-heading font-light">{deduction.name}</CardTitle>
                  <CardDescription>Max potential: ${deduction.max_amount.toLocaleString()}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Category: {deduction.category}</span>
                    </div>
                    <Progress value={0} className="h-2" />
                    <p className="text-xs text-muted-foreground">
                      Track expenses in this category to maximize your deduction
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Claimed Deductions */}
      {deductions.claimed_deductions.length > 0 && (
        <div className="space-y-6">
          <div className="flex items-center space-x-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            <h2 className="text-2xl font-heading font-light">Your Claimed Deductions</h2>
          </div>
          <div className="space-y-4">
            {deductions.claimed_deductions.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card data-testid={`claimed-deduction-${item.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-medium mb-1">{item.description}</h3>
                        <p className="text-sm text-muted-foreground">{item.category}</p>
                      </div>
                      <p className="text-2xl font-mono font-semibold text-primary" data-testid={`deduction-amount-${item.id}`}>
                        ${item.amount.toLocaleString()}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {deductions.claimed_deductions.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-medium mb-2">No deductions claimed yet</h3>
            <p className="text-muted-foreground">
              Mark expenses as deductible in the Expense Tracker to see them here
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
